import React from 'react';
import moment from 'moment';
import { CountryCode } from 'libphonenumber-js';
import cx from 'classnames';

import { capitalizeAvatarName, concatFullName } from 'utils/common';
import {
  formatPhoneNumberWithCountryCode,
  geCountryByPhoneNumber,
} from 'utils/formatPhoneNumber';
import { IFamilyBookingAppointment } from 'interfaces/familyBookingTypes';
import { BookableSelectedPatient } from 'services/APIs';

import styles from './UnbookablePatientCard.module.scss';

interface UnbookablePatientCardProps {
  patient: BookableSelectedPatient;
  isPointOfContact: boolean;
  colorIndex: number;
}

type PatientInfoField = 'dob' | 'email' | 'phoneNumber' | 'lastHygieneAppt';

type AppointmentInfoField = 'service' | 'appointmentDate' | 'doctor';

interface IInfoField<T> {
  label: string;
  property: T;
}

const PATIENT_INFO_FIELDS: IInfoField<PatientInfoField>[] = [
  { label: 'Date of birth', property: 'dob' },
  { label: 'Email', property: 'email' },
  { label: 'Phone number', property: 'phoneNumber' },
  { label: 'Last cleaning', property: 'lastHygieneAppt' },
];

const APPOINTMENT_INFO_FIELDS: IInfoField<AppointmentInfoField>[] = [
  { label: 'Date & time', property: 'appointmentDate' },
  { label: 'Reason', property: 'service' },
  { label: 'Practitioner', property: 'doctor' },
];

const UnbookablePatientCard = ({
  patient,
  isPointOfContact,
  colorIndex,
}: UnbookablePatientCardProps) => {
  const avatarText = capitalizeAvatarName(
    `${patient.firstName.replace(/\s/g, '').trim()} ${patient.lastName
      .replace(/\s/g, '')
      .trim()}`
  );

  const appointment: IFamilyBookingAppointment | undefined =
    patient.appointment || patient.futureAppointments?.[0];

  const renderPatientInfo = (property: PatientInfoField) => {
    if (property === 'phoneNumber') {
      const countryCode = geCountryByPhoneNumber(patient.phoneNumber);

      return formatPhoneNumberWithCountryCode(
        patient.phoneNumber,
        countryCode as CountryCode
      );
    }

    if (property === 'dob') {
      return patient.dob
        ? moment(patient.dob, 'YYYY-MM-DD').format('MMM DD, YYYY')
        : '';
    }

    if (property === 'lastHygieneAppt') {
      return patient.lastHygieneAppt?.startDate
        ? moment(patient.lastHygieneAppt.startDate, 'YYYY-MM-DD').format(
            'MMM DD, YYYY'
          )
        : '';
    }

    return patient.email;
  };

  const renderAppointmentInfo = (property: AppointmentInfoField) => {
    if (!appointment) return '';

    if (property === 'appointmentDate') {
      const appointmentDate = moment(
        appointment.appointmentDate,
        'YYYY-MM-DD'
      ).format('MMM DD, YYYY');

      const startTime = moment(appointment.startTime, 'HH:mm:ss').format(
        'hh:mm A'
      );

      return `${appointmentDate} - ${startTime}`;
    }

    return appointment[property] || '-';
  };

  const isInPast = appointment?.isInPast || false;

  const visiblePatientInfoFields = PATIENT_INFO_FIELDS.filter(
    (item) =>
      item.property !== 'lastHygieneAppt' || !!renderPatientInfo(item.property)
  );

  const renderInfoRow = (label: string, value: React.ReactNode) => (
    <div key={label} className={styles['user-info']}>
      <span className={styles['user-detail']}>{label}:</span>

      <span className={styles['user-data']}>{value}</span>
    </div>
  );

  const containerStyle = cx({
    [styles['container']]: true,
    [styles['container-poc']]: isPointOfContact,
  });

  const avatarClassName = cx(
    styles['avatar'],
    styles[`avatar-color-${colorIndex}`]
  );

  return (
    <div className={containerStyle}>
      {isPointOfContact && (
        <div className={styles['point-of-contact']}>POINT OF CONTACT</div>
      )}
      <div className={styles['left-section']}>
        <div className={avatarClassName}>{avatarText}</div>

        <div className={styles['full-name']}>
          {concatFullName(patient.firstName, patient.lastName)}
        </div>
      </div>
      <div className={styles['right-section']}>
        {visiblePatientInfoFields.map((item) =>
          renderInfoRow(item.label, renderPatientInfo(item.property))
        )}

        <div className={styles['appt-heading']}>
          {isInPast ? 'Incomplete Appointment' : 'Future Appointment'}
        </div>

        {APPOINTMENT_INFO_FIELDS.map((item) =>
          renderInfoRow(item.label, renderAppointmentInfo(item.property))
        )}
      </div>
    </div>
  );
};

export default UnbookablePatientCard;
